import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';

import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';

import SearchService from '~/services/SearchService';

import PolygonMergeForm from './PolygonMergeForm';
import PolygonMergePreviewMap, { MergedGeometry, PlaceGeometry } from './PolygonMergePreviewMap';

interface SearchEntry {
  region: string;
  brand: string;
  total_searches: number;
}

interface PlaceMetadata {
  createdAt: string | null;
  updatedAt: string | null;
  searches: SearchEntry[];
}

interface PreviewData {
  target: PlaceGeometry;
  source: PlaceGeometry;
  merged: MergedGeometry;
  areaChangeKm2: number;
}

interface PlaceData {
  geometry: PlaceGeometry;
  metadata: PlaceMetadata;
}

interface PlaceResultExtended {
  createdAt?: string | null;
  updatedAt?: string | null;
  searches?: SearchEntry[];
}

function formatDate(value: string | null): string {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString();
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

async function fetchPlaceData(placeId: string): Promise<PlaceData | null> {
  try {
    const response = await SearchService.getPlace(placeId);
    const place = response.result as typeof response.result & PlaceResultExtended;
    return {
      geometry: {
        placeId: place.placeId,
        name: place.name ?? place.nameFull ?? placeId,
        type: place.type,
        geojson: place.geom,
        areaKm2: 0,
      },
      metadata: {
        createdAt: place.createdAt ?? null,
        updatedAt: place.updatedAt ?? null,
        searches: place.searches ?? [],
      },
    };
  } catch {
    return null;
  }
}

function SearchSummary({ searches }: { searches: SearchEntry[] }) {
  const total = searches.reduce((sum, s) => sum + s.total_searches, 0);
  const leSearches = searches.filter((s) => s.brand === 'luxuryescapes');
  const leTotal = leSearches.reduce((sum, s) => sum + s.total_searches, 0);
  const topRegions = leSearches.sort((a, b) => b.total_searches - a.total_searches).slice(0, 3);

  return (
    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" gap={3}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Total searches
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {total.toLocaleString()}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Luxury Escapes
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {leTotal.toLocaleString()}
          </Typography>
        </Box>
      </Stack>
      {topRegions.length > 0 && (
        <Stack direction="row" gap={2} sx={{ mt: 0.5 }}>
          {topRegions.map((s) => (
            <Typography key={s.region} variant="caption" color="text.secondary">
              {s.region}: {s.total_searches.toLocaleString()}
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function PlaceCard({
  label,
  color,
  geom,
  meta,
}: {
  label: string;
  color: string;
  geom: PlaceGeometry;
  meta: PlaceMetadata;
}) {
  return (
    <Box sx={{ p: 2, border: '2px solid', borderColor: color, borderRadius: 1 }}>
      <Stack direction="row" gap={1} alignItems="center" mb={1}>
        <Typography variant="subtitle2">{label}</Typography>
        <Chip label={geom.type} size="small" sx={{ borderColor: color, color }} variant="outlined" />
      </Stack>
      <Typography variant="body1" fontWeight="bold">
        {geom.name}
      </Typography>
      {geom.areaKm2 > 0 && (
        <Typography variant="body2" color="text.secondary">
          {geom.areaKm2.toLocaleString()} km²
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        Updated: {formatDate(meta.updatedAt)}
      </Typography>
      {meta.searches.length > 0 && <SearchSummary searches={meta.searches} />}
    </Box>
  );
}

export default function PolygonMergeContainer() {
  const [isLoading, setLoading] = useState(false);
  const [targetGeom, setTargetGeom] = useState<PlaceGeometry | null>(null);
  const [sourceGeom, setSourceGeom] = useState<PlaceGeometry | null>(null);
  const [targetMeta, setTargetMeta] = useState<PlaceMetadata | null>(null);
  const [sourceMeta, setSourceMeta] = useState<PlaceMetadata | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [isConfirmOpen, setConfirmOpen] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const handleTargetChange = useCallback(
    async (placeId: string | null) => {
      setPreviewData(null);
      if (!placeId) {
        setTargetGeom(null);
        setTargetMeta(null);
        return;
      }
      const data = await fetchPlaceData(placeId);
      if (data) {
        setTargetGeom(data.geometry);
        setTargetMeta(data.metadata);
      } else {
        enqueueSnackbar(`Failed to load geometry for ${placeId}`, { variant: 'error' });
        setTargetGeom(null);
        setTargetMeta(null);
      }
    },
    [enqueueSnackbar],
  );

  const handleSourceChange = useCallback(
    async (placeId: string | null) => {
      setPreviewData(null);
      if (!placeId) {
        setSourceGeom(null);
        setSourceMeta(null);
        return;
      }
      const data = await fetchPlaceData(placeId);
      if (data) {
        setSourceGeom(data.geometry);
        setSourceMeta(data.metadata);
      } else {
        enqueueSnackbar(`Failed to load geometry for ${placeId}`, { variant: 'error' });
        setSourceGeom(null);
        setSourceMeta(null);
      }
    },
    [enqueueSnackbar],
  );

  const handleSwap = useCallback(() => {
    setTargetGeom(sourceGeom);
    setSourceGeom(targetGeom);
    setTargetMeta(sourceMeta);
    setSourceMeta(targetMeta);
    setPreviewData(null);
  }, [targetGeom, sourceGeom, targetMeta, sourceMeta]);

  const handlePreview = useCallback(
    async (targetPlaceId: string, sourcePlaceId: string) => {
      setLoading(true);
      setPreviewData(null);
      try {
        const response = await SearchService.previewGeometryMerge(targetPlaceId, sourcePlaceId);
        const result = response.result;
        setPreviewData({
          ...result,
          areaChangeKm2: result.merged.areaKm2 - result.target.areaKm2,
        });
      } catch (e) {
        enqueueSnackbar(`Failed to preview merge: ${errorMessage(e)}`, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  const handleApplyRequest = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleConfirmApply = useCallback(async () => {
    if (!previewData) return;
    setConfirmOpen(false);
    setLoading(true);
    try {
      await SearchService.applyGeometryMerge(previewData.target.placeId, previewData.source.placeId);
      enqueueSnackbar('Geometry merge applied successfully', { variant: 'success' });
      setPreviewData(null);
    } catch (e) {
      enqueueSnackbar(`Failed to apply merge: ${errorMessage(e)}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [previewData, enqueueSnackbar]);

  const displayTarget = previewData?.target ?? targetGeom;
  const displaySource = previewData?.source ?? sourceGeom;
  const displayMerged = previewData?.merged ?? null;
  const isNoOp = previewData?.areaChangeKm2 === 0;
  const hasCards = !!(displayTarget && targetMeta) || !!(displaySource && sourceMeta);

  return (
    <Container>
      <Helmet>
        <title>Polygon Merge</title>
      </Helmet>

      <Stack direction="column" gap={3}>
        <PageSubheader title="Polygon Merge" />

        <Typography variant="body2" color="text.secondary">
          Expand a place&apos;s boundary by merging another place&apos;s polygon into it. Preview the result on the map
          before applying.
        </Typography>

        <PolygonMergeForm
          onPreview={handlePreview}
          onTargetChange={handleTargetChange}
          onSourceChange={handleSourceChange}
          onSwap={handleSwap}
          isLoading={isLoading}
        />

        {isLoading && <Spinner />}

        {hasCards ? (
          <Stack direction={{ xs: 'column', lg: 'row' }} gap={3}>
            <Stack direction="column" gap={2} sx={{ minWidth: 320, maxWidth: { lg: 380 } }}>
              {displayTarget && targetMeta && (
                <PlaceCard label="Expand this place" color="#1976d2" geom={displayTarget} meta={targetMeta} />
              )}
              {displaySource && sourceMeta && (
                <PlaceCard label="Include area from" color="#2e7d32" geom={displaySource} meta={sourceMeta} />
              )}
              {previewData && !isNoOp && (
                <Box
                  sx={{ p: 2, border: '2px solid', borderColor: '#ed6c02', borderRadius: 1, bgcolor: 'action.hover' }}
                >
                  <Typography variant="subtitle2" color="#ed6c02" gutterBottom>
                    Result
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {previewData.target.name} will expand
                  </Typography>
                  <Typography variant="body2">
                    from {previewData.target.areaKm2.toLocaleString()} km² &rarr;{' '}
                    {previewData.merged.areaKm2.toLocaleString()} km²{' '}
                    <strong>(+{previewData.areaChangeKm2.toLocaleString()} km²)</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    by including area from {previewData.source.name} ({previewData.source.type})
                  </Typography>
                </Box>
              )}
              {previewData && isNoOp && (
                <Box
                  sx={{
                    p: 2,
                    border: '2px solid',
                    borderColor: 'warning.main',
                    borderRadius: 1,
                    bgcolor: 'warning.light',
                  }}
                >
                  <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                    No change needed
                  </Typography>
                  <Typography variant="body2" color="warning.dark">
                    {previewData.source.name} is already fully covered by {previewData.target.name}. Merging would not
                    change the boundary.
                  </Typography>
                </Box>
              )}
              {previewData && !isNoOp && (
                <Button variant="contained" color="warning" fullWidth onClick={handleApplyRequest}>
                  Apply Merge
                </Button>
              )}
            </Stack>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PolygonMergePreviewMap target={displayTarget} source={displaySource} merged={displayMerged} />
            </Box>
          </Stack>
        ) : (
          <PolygonMergePreviewMap target={displayTarget} source={displaySource} merged={displayMerged} />
        )}
      </Stack>

      <Dialog open={isConfirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Geometry Merge</DialogTitle>
        <DialogContent>
          {previewData && (
            <Stack direction="column" gap={2}>
              <Typography variant="body1">
                <strong>{previewData.target.name}</strong> ({previewData.target.type}) will be expanded by including
                area from <strong>{previewData.source.name}</strong> ({previewData.source.type}).
              </Typography>
              <Typography variant="body2">
                Area: {previewData.target.areaKm2.toLocaleString()} km² &rarr;{' '}
                {previewData.merged.areaKm2.toLocaleString()} km² (+{previewData.areaChangeKm2.toLocaleString()} km²)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This can be re-applied if Expedia overwrites the geometry on the next import.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={handleConfirmApply}>
            Apply Merge
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
